import { isGlobalViewer } from '../../../constants/roles';
// src/modules/treasury/pages/PaymentApprovalDashboard.jsx
// Operational monitoring screen for treasury payment requests.
// Approval actions are exclusively handled through Workflow → My Approvals.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Space, Button, Typography, Spin, Alert,
  Row, Col, Select, DatePicker, Badge, Tooltip,
} from 'antd';
import {
  ReloadOutlined, FileTextOutlined, ApartmentOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import { fetchPaymentRequests } from '../api';
import {
  APPROVAL_STATUS,
  PAYMENT_MODE,
  TREASURY_MODULE,
  APPROVAL_STATUS_OPTIONS,
} from '../constants/treasuryConstants';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

const MODULE_LABELS = {
  [TREASURY_MODULE.WALK_IN]: 'Walk-In',
  [TREASURY_MODULE.PLEDGE_ADVANCE]: 'Pledge Advance',
  [TREASURY_MODULE.PLEDGE_SETTLEMENT]: 'Pledge Settlement',
  [TREASURY_MODULE.MANUAL_ADJUSTMENT]: 'Adjustment',
  [TREASURY_MODULE.EOD]: 'EOD',
};

const MODE_COLORS = {
  [PAYMENT_MODE.CASH]: 'gold',
  [PAYMENT_MODE.BANK]: 'blue',
};

const STATUS_COLORS = {
  [APPROVAL_STATUS.PENDING]: 'orange',
  [APPROVAL_STATUS.AUTO_APPROVED]: 'green',
  [APPROVAL_STATUS.APPROVED]: 'green',
  [APPROVAL_STATUS.REJECTED]: 'red',
};

const WF_STATUS_COLORS = {
  pending: 'orange',
  approved: 'green',
  completed: 'green',
  rejected: 'red',
  escalated: 'purple',
};

function WorkflowStatusCell({ instance }) {
  if (!instance) {
    return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
  }

  const currentStepData = instance.steps?.[instance.currentStep - 1];
  const approvers = currentStepData?.approvers || [];
  const approverNames = approvers.map((a) => a?.name || a?.email || '?').join(', ');

  return (
    <Space direction="vertical" size={0}>
      <Tag color={WF_STATUS_COLORS[instance.status] || 'default'} style={{ margin: 0 }}>
        {instance.status?.toUpperCase()}
      </Tag>
      {instance.status === 'pending' && approverNames && (
        <Tooltip title={`Pending with: ${approverNames}`}>
          <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
            {approverNames}
          </Text>
        </Tooltip>
      )}
    </Space>
  );
}

export default function PaymentApprovalDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [userBranch, setUserBranch] = useState(null);
  const [isHO, setIsHO] = useState(false);
  const [branches, setBranches] = useState([]);

  const [filters, setFilters] = useState({
    branchId: null,
    approvalStatus: null,
    dateRange: null,
  });

  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });

  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Init: resolve branch type ──────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        if (!user?.branch) { setInitLoading(false); return; }
        const branchRes = await fetchBranchById(user.branch);
        const branch = branchRes?.data || branchRes;
        setUserBranch(branch);

        const ho = branch?.branchType === 'HO' || isGlobalViewer(user);
        setIsHO(ho);

        if (ho) {
          const bRes = await fetchBranches({ limit: 100 });
          const list = bRes?.data?.data || bRes?.data || [];
          setBranches(list);
        }
      } catch {
        setError('Failed to load branch profile');
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchRequests = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: pagination.pageSize };
      if (filters.branchId) params.branchId = filters.branchId;
      if (filters.approvalStatus) params.approvalStatus = filters.approvalStatus;
      if (filters.dateRange?.[0]) params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
      if (filters.dateRange?.[1]) params.endDate = filters.dateRange[1].format('YYYY-MM-DD');

      const res = await fetchPaymentRequests(params);
      const data = res?.data;
      setRequests(data?.data || []);
      setPagination(prev => ({
        ...prev,
        current: data?.pagination?.current || 1,
        total: data?.pagination?.total || 0,
      }));
    } catch (err) {
      setError(err.message || 'Failed to load payment requests');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize]);

  useEffect(() => {
    if (!initLoading) fetchRequests(1);
  }, [filters, initLoading]);

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Date',
      dataIndex: 'businessDate',
      width: 100,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      width: 120,
      render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text>,
    },
    isHO && {
      title: 'Branch',
      dataIndex: ['branch', 'name'],
      width: 140,
      render: (v, row) => row.branch?.name || '—',
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      width: 150,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Case Ref',
      dataIndex: 'caseReference',
      width: 120,
      render: (v) => v ? <Text code style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Module',
      dataIndex: 'module',
      width: 130,
      render: (v) => <Tag>{MODULE_LABELS[v] || v}</Tag>,
    },
    {
      title: 'Mode',
      dataIndex: 'paymentMode',
      width: 90,
      render: (v) => <Tag color={MODE_COLORS[v]}>{v}</Tag>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      width: 120,
      align: 'right',
      render: (v) => <Text strong>₹{fmt(v)}</Text>,
    },
    {
      title: 'Treasury Status',
      dataIndex: 'approvalStatus',
      width: 150,
      render: (v, row) => (
        <Space direction="vertical" size={0}>
          <Tag color={STATUS_COLORS[v]}>{v?.replace(/_/g, ' ')}</Tag>
          {v === APPROVAL_STATUS.AUTO_APPROVED && row.autoApprovalReason && (
            <Text type="secondary" style={{ fontSize: 11 }}>{row.autoApprovalReason}</Text>
          )}
          {v === APPROVAL_STATUS.REJECTED && row.rejectionReason && (
            <Tooltip title={row.rejectionReason}>
              <Text type="danger" style={{ fontSize: 11 }} ellipsis>
                {row.rejectionReason}
              </Text>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Workflow Status',
      key: 'workflowStatus',
      width: 160,
      render: (_, row) => <WorkflowStatusCell instance={row.workflowInstance} />,
    },
    {
      title: 'Requested By',
      dataIndex: ['requestedBy', 'name'],
      width: 130,
      render: (v, row) => row.requestedBy?.name || '—',
    },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      width: 100,
      render: (v) => v ? dayjs(v).format('HH:mm') : '—',
    },
    {
      title: 'Workflow',
      key: 'workflowAction',
      width: 120,
      fixed: 'right',
      render: (_, row) => {
        if (row.workflowInstance) {
          return (
            <Button
              size="small"
              icon={<ApartmentOutlined />}
              onClick={() => navigate(`/approvals/instance/${row.workflowInstance._id}`)}
            >
              View
            </Button>
          );
        }
        if (row.approvalStatus === APPROVAL_STATUS.PENDING) {
          return (
            <Button
              size="small"
              icon={<ApartmentOutlined />}
              onClick={() => navigate('/approvals/my-approvals')}
            >
              My Approvals
            </Button>
          );
        }
        return null;
      },
    },
  ].filter(Boolean);

  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  const pendingCount = requests.filter(r => r.approvalStatus === APPROVAL_STATUS.PENDING).length;

  return (
    <div style={{ padding: '16px 0' }}>
      {/* ── Header ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space align="baseline">
            <Title level={5} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: 8 }} />
              Payment Queue
            </Title>
            {pendingCount > 0 && (
              <Badge count={pendingCount} style={{ backgroundColor: '#fa8c16' }} />
            )}
          </Space>
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            {isHO
              ? 'Monitoring view — approve or reject payments in Workflow → My Approvals'
              : `${userBranch?.name || ''} — read-only`}
          </Text>
        </Col>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchRequests(1)}
            loading={loading}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />
      )}

      {/* ── Filters ── */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 8]} align="middle">
          {isHO && (
            <Col xs={24} sm={8}>
              <Select
                placeholder="All branches"
                allowClear
                showSearch
                style={{ width: '100%' }}
                value={filters.branchId}
                onChange={(v) => setFilters(f => ({ ...f, branchId: v || null }))}
                optionFilterProp="label"
                options={branches.map(b => ({ value: b._id, label: `${b.name} (${b.code})` }))}
              />
            </Col>
          )}
          <Col xs={24} sm={isHO ? 7 : 10}>
            <Select
              placeholder="All statuses"
              allowClear
              style={{ width: '100%' }}
              value={filters.approvalStatus}
              onChange={(v) => setFilters(f => ({ ...f, approvalStatus: v || null }))}
              options={APPROVAL_STATUS_OPTIONS.map(o => ({
                value: o.value,
                label: <Tag color={o.color}>{o.label}</Tag>,
              }))}
            />
          </Col>
          <Col xs={24} sm={isHO ? 9 : 14}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => setFilters(f => ({ ...f, dateRange: dates }))}
              format="DD/MM/YYYY"
            />
          </Col>
        </Row>
      </Card>

      {/* ── Table ── */}
      <Table
        dataSource={requests}
        columns={columns}
        rowKey="_id"
        loading={loading}
        size="small"
        scroll={{ x: isHO ? 1450 : 1200 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (t) => `${t} requests`,
          onChange: (page) => fetchRequests(page),
        }}
        rowClassName={(row) =>
          row.approvalStatus === APPROVAL_STATUS.PENDING ? 'ant-table-row-pending' : ''
        }
        locale={{ emptyText: 'No payment requests found' }}
      />
    </div>
  );
}
